import strings from 'l10n'
import { forEach, kebabCase } from 'lodash'
import sha256 from 'crypto-js/sha256'
import { loadFirstToken, loadToken } from 'context/UserSession/userSessionReducer'
import { StringMap } from 'types/common'
import { TabData } from 'components/ui/navigation/Tabs'
import { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { AutocompleteInfo, AutocompleteOptions, AutocompleteResponse } from 'types/autocomplete'
import regex from './regex'
import {
  ACCRUED_FEES_KEY,
  ACCRUED_INTEREST_KEY,
  AP_BANK_ACCOUNT_KEY,
  AR_BANK_ACCOUNT_KEY,
  CASH_RESERVE_KEY,
  ESCROW_RESERVE_KEY,
  FEES_KEY,
  FUEL_ADVANCE_KEY,
  LIABILITY_KEY,
  OPEN_AR_KEY,
  OVER_ADVANCE_KEY,
  UNAPPLIED_KEY,
  WRITE_OFFS_KEY,
} from 'constants/ledgerNameKeys'
import { currencyFormat, toDecPercent } from './format'
import { IFilterList } from 'components/ui/dataDisplay/DataTable'
import { LS_FACTOR_SETTINGS } from 'constants/localStorageKeys'
import { CreditLimitInfoResponse } from 'types/creditLimit'
import { AxiosResponseHeaders } from 'axios'
import { PAGINATION_HAS_NEXT_PAGE } from 'constants/headers'

export function minValueLimit(value: number, text?: string) {
  const message =
    text ||
    `${strings.formatString(strings.NUMBER_MUST_BE_GREATER_EQUALS_THAN, {
      number: value,
    })}`
  return {
    value: value,
    message: message,
  }
}

export function maxValueLimit(value?: number | string | null, text?: string) {
  if (!isDefined(value) || value === '') return undefined
  const message =
    text ||
    `${strings.formatString(strings.NUMBER_MUST_BE_LESS_EQUALS_THAN, {
      number: value,
    })}`

  return {
    value: value,
    message: message,
  }
}

export const INVOICE_DATE_MIN = '1900-01-01'
export const INVOICE_DATE_MAX_YEARS = 2

export const addYearsToDateString = (dateString: string, years: number) => {
  if (!dateString) return ''
  const datePart = dateString.split('T')[0]
  const [yearStr, monthStr, dayStr] = datePart.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return datePart
  }
  const targetYear = year + years
  const daysInTargetMonth = new Date(targetYear, month, 0).getDate()
  const clampedDay = Math.min(day, daysInTargetMonth)
  const yyyy = targetYear
  const mm = String(month).padStart(2, '0')
  const dd = String(clampedDay).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export const addDaysToDateString = (dateString: string, days: number) => {
  if (!dateString) return ''
  const datePart = dateString.split('T')[0]
  const [yearStr, monthStr, dayStr] = datePart.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const day = Number(dayStr)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(days)) {
    return datePart
  }
  const target = new Date(year, month - 1, day)
  target.setDate(target.getDate() + days)
  const yyyy = target.getFullYear()
  const mm = String(target.getMonth() + 1).padStart(2, '0')
  const dd = String(target.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export const minDateLimit = (date: string, current: boolean) => {
  let showDate = date
  if (current) {
    showDate = strings.CURRENT_DATE.toLowerCase()
  }
  return {
    value: date,
    message: `${strings.formatString(strings.DATE_CANNON_BE_EARLIER_THAN, {
      date: showDate,
    })}`,
  }
}

export const maxDateLimit = (date: string, current: boolean) => {
  let showDate = date
  if (current) {
    showDate = strings.CURRENT_DATE.toLowerCase()
  }
  return {
    value: date,
    message: `${strings.formatString(strings.DATE_CANNON_BE_LATER_THAN, {
      date: showDate,
    })}`,
  }
}

export const isDateBeforeToday = (date: string) => {
  return new Date(date) < new Date()
}

export function isMaxInvoiceAmountBypassActive(bypassExpiration?: string | null) {
  return isDefined(bypassExpiration) && new Date(bypassExpiration).getTime() > Date.now()
}

export function getCurrentToken() {
  const firstToken = loadFirstToken()
  const loginToken = loadToken()
  const currentToken = firstToken ? firstToken : loginToken
  return currentToken
}

export function getTokenContent(token: string | null) {
  const base64Url = token?.split('.')[1]
  const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/')
  const tokenContent = JSON.parse(atob(base64 ?? ''))
  return tokenContent
}

export function getDocumentDownloadUrl(fileUrl: string, params: StringMap = {}) {
  const jwtToken = loadToken()
  const factorId = getTokenContent(jwtToken).factorId

  if (factorId) params['factorId'] = factorId
  if (jwtToken) params['authToken'] = jwtToken

  const url = new URL(fileUrl)
  forEach(params, (value, key) => {
    url.searchParams.append(key, value)
  })
  return url.toString()
}

export function getDisbursementDownloadUrl(fileUrl: string, disbursementId: string) {
  return getDocumentDownloadUrl(fileUrl, { disbursementId })
}

export function triggerDisbursementDownload(fileUrl: string, disbursementId: string) {
  const url = getDisbursementDownloadUrl(fileUrl, disbursementId)
  window.open(url, '_blank')
}

export function triggerFactorcloudDocumentDownload(fileUrl: string, params: StringMap = {}) {
  const url = getDocumentDownloadUrl(fileUrl, params)
  window.open(url, '_blank')
}

// Opens the document in a new tab for the user to view/interact. Passes inline=true so the backend serves it
// with Content-Disposition: inline (for browser-renderable, safe types), letting the browser render it natively
// instead of forcing a download.
export function openFactorcloudDocumentInNewTab(fileUrl: string, params: StringMap = {}) {
  const url = getDocumentDownloadUrl(fileUrl, { ...params, inline: 'true' })
  window.open(url, '_blank')
}

export function slugify(arg: string) {
  return kebabCase(arg)
}

export function hash(arg: any) {
  const str = JSON.stringify(arg)
  const hash = sha256(str).toString()
  return hash
}

/**
 * Returns true is val is not null nor undefined
 * @param arg to check
 * @returns boolean result
 */
export function isDefined<T>(arg: T | null | undefined): arg is T {
  return typeof arg != 'undefined' && arg !== null
}

/**
 * Returns true if arg is null, undefined or ""
 * @param arg to check
 * @returns boolean result
 */
export function empty(arg: string | null | undefined) {
  return isDefined(arg) !== true || arg === ''
}

/**
 * Returns val if condition is True. Return undefined otherwise
 * @param arg to be returned
 * @param condition to check
 * @returns val or undefined
 */
export function ift<T>(arg: T, condition: boolean) {
  if (condition) return arg
}

export function formatDate(invoiceDate: string) {
  const currentDate = new Date().toISOString().split('T')[1]
  return invoiceDate.concat('T').concat(currentDate)
}

export function isUpdatedPaymentDate(invoiceDate: string, dataInvoiceDate: string) {
  if (!invoiceDate) {
    return dataInvoiceDate ? formatDate(dataInvoiceDate) : undefined
  }

  return invoiceDate.split('T')[0] === dataInvoiceDate
    ? invoiceDate
    : dataInvoiceDate
      ? formatDate(dataInvoiceDate)
      : undefined
}

export function minLengthLimit(value: number, text?: string) {
  const message =
    text ||
    `${strings.formatString(strings.TEXT_MUST_BE_GREATER_THAN, {
      number: value,
    })}`
  return {
    value: value,
    message: message,
  }
}

export function maxLengthLimit(value: number, text?: string) {
  const message =
    text ||
    `${strings.formatString(strings.TEXT_MUST_BE_LESS_THAN, {
      number: value,
    })}`

  return {
    value: value,
    message: message,
  }
}

export function isBlank(value: string | null) {
  return value === null || value.trim() === ''
}

export function normalizeEmailList(emailList?: string[] | string | null) {
  if (!isDefined(emailList)) return []
  if (Array.isArray(emailList)) return emailList.map((email) => email.trim()).filter((email) => email !== '')
  return emailList
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email !== '')
}

export function formatEmailList(emailList?: string[] | string | null) {
  return normalizeEmailList(emailList).join(', ')
}

export function isNotBlank(value?: string | null) {
  return isDefined(value) && !isBlank(value)
}

/**
 * Review the two values and return the first one that is not null.
 * @param value1 or @param value2 to be returned
 * @returns the corresponding value or undefined
 */
export function getNotNullValue<T>(value1: T, value2: T) {
  return isDefined(value1) ? value1 : isDefined(value2) ? value2 : undefined
}

export function getTabIndex(tabs: TabData[], value: String) {
  return tabs.findIndex((tab) => tab.label === value)
}

/**
 * Returns the array of columns without the columns that are in the columnFieldNames array
 * @param array of columns to check @param columnFieldNames to be removed from @param array
 * @returns the filtered array of columns
 */

export function removeColumnFromTable(array: GridColDef[], columnFieldNames: String[]) {
  let newArray = array
  columnFieldNames.forEach((columnFieldName) => {
    newArray = newArray.filter((column: GridColDef) => column.field !== columnFieldName)
  })
  return newArray
}

export function updateSelectedElements(selected: any, prevSelected: any, selectedIds?: string[]) {
  //Remove from prev if the element is not selected. For this we compare the ids on array. If the element is not in the ids array, we remove it from prev.
  const newPrev =
    prevSelected.length > 0 ? prevSelected.filter((element: any) => selectedIds?.includes(element.id)) : []
  /*Add only the elements that are not in prev. The Table only return the selected rows per page. So, if you back to a previous page,
    the table send the previous selected rows again. We need to filter the elements that are already in prev.*/
  const newSelected = selected.filter(
    (element: any) => !prevSelected.find((prevElement: any) => prevElement.id === element.id)
  )
  //Concat the new prev and new selected invoices to get the final selected invoices
  const finalSelectedElements = [...newPrev, ...newSelected]
  return finalSelectedElements
}

export function buildUri(...components: string[]) {
  return components.map((c) => encodeURIComponent(c)).join('/')
}

export function getNextDayFromDate(dateString: string) {
  const currentDate = new Date(dateString.split('T')[0])
  const nextDay = new Date(currentDate)
  nextDay.setDate(nextDay.getDate() + 1)
  return nextDay.toISOString().split('T')[0]
}

export function getAutocompleteListValues(value?: AutocompleteResponse) {
  return value?.data.map((item: AutocompleteInfo) => {
    return {
      key: item.id,
      name: item.name,
      value: item.nInvoices,
      companyType: item.companyType,
    }
  })
}

export function handleZipLabel(countryCode?: string) {
  if (countryCode === 'US' || isDefined(countryCode) === false) {
    return strings.ZIP
  }
  return strings.POSTAL_CODE
}

export function environment() {
  const apiUrl = process.env.REACT_APP_API_URL
  if (apiUrl?.includes('api.dev')) return 'DEVELOPMENT'
  if (apiUrl?.includes('api.app') || apiUrl?.includes('api.integration-eu')) return 'PRODUCTION'
  if (apiUrl?.includes('api.int')) return 'SANDBOX'
  if (apiUrl?.includes('localhost')) return 'LOCAL'
  return ''
}

export function handleZipPostalCodeLength(countryCode?: string) {
  if (countryCode === 'CA' || isDefined(countryCode) === false) {
    return 7
  }
  return 10
}

export function handleZipLabelMaxLengthMessage(countryCode?: string) {
  if (countryCode === 'CA' || isDefined(countryCode) === false) {
    return strings.MAX_LENGTH_POSTAL_CODE
  }
  return strings.MAX_LENGTH_ZIP_CODE
}

export function handleZipCodeRegex(countryCode?: string) {
  switch (countryCode) {
    case 'CA':
      return regex.POSTAL_CODE_REGEX_PATTERN_CANADA
    case 'UK':
      return regex.POSTAL_CODE_REGEX_PATTERN_UK
    case 'MX':
      return regex.POSTAL_CODE_REGEX_PATTERN_MEXICO
    case 'AUD':
      return regex.POSTAL_CODE_REGEX_PATTERN_AUSTRALIA
    case 'US':
      return regex.ZIP_CODE_REGEX_PATTERN
    default:
      return regex.POSTAL_ANY_CHARACTERS
  }
}

export function handleRegexErrorMessage(countryCode?: string) {
  if (isDefined(countryCode) && countryCode !== 'US') {
    return strings.INVALID_POSTAL_CODE
  }
  return strings.INVALID_ZIP_CODE
}

export function getIndexOfColumn(column: string, columns: GridColDef[]) {
  return columns.findIndex((col) => col.field === column)
}

type SelectedClientOrBroker = string | AutocompleteOptions | null | undefined

// Autocomplete responses only hold the latest search page, so the type comes from the selected option itself;
// the response lookup remains for values that were not picked from the autocomplete (e.g. preset ids).
function getSelectedCompanyType(selected: SelectedClientOrBroker, clientBrokerState?: AutocompleteResponse) {
  if (selected && typeof selected === 'object' && selected.companyType) return selected.companyType
  const companyClientOrBrokerId = selected && typeof selected === 'object' ? selected.value : selected
  return clientBrokerState?.data?.find((item) => item.id === companyClientOrBrokerId)?.companyType
}

export function isClient(selected: SelectedClientOrBroker, clientBrokerState?: AutocompleteResponse) {
  return getSelectedCompanyType(selected, clientBrokerState) !== 'BROKER'
}

/** Preset autocomplete value for a record that stores its client/broker as ids plus names. */
export function clientOrBrokerOption(record?: {
  companyClientId?: string | null
  companyClientName?: string | null
  companyBrokerId?: string | null
  companyBrokerName?: string | null
}): AutocompleteOptions | null {
  if (record?.companyClientId) {
    return { value: record.companyClientId, label: record.companyClientName ?? '', companyType: 'CLIENT' }
  }
  if (record?.companyBrokerId) {
    return { value: record.companyBrokerId, label: record.companyBrokerName ?? '', companyType: 'BROKER' }
  }
  return null
}

/** Preset autocomplete value for a company picked in a side list; falls back to the id when its name is unknown. */
export function companyOption(id?: string | null, name?: string | null, companyType?: string) {
  if (!id) return undefined
  return name ? { value: id, label: name, companyType } : id
}

export function isBrokerOrClient(
  selected: SelectedClientOrBroker,
  clientBrokerState?: AutocompleteResponse
): 'CLIENT' | 'BROKER' | '' {
  const companyType = getSelectedCompanyType(selected, clientBrokerState)
  return companyType === 'BROKER' || companyType === 'CLIENT' ? companyType : ''
}

export const getLedgerKey = (ledgerType: string) => {
  if (!isDefined(ledgerType) || isBlank(ledgerType)) return ''
  switch (ledgerType) {
    case 'AR':
      return OPEN_AR_KEY
    case 'FEE':
      return FEES_KEY
    case 'ACCRUED_FEE':
      return ACCRUED_FEES_KEY
    case 'ESCROW':
      return ESCROW_RESERVE_KEY
    case 'CASH_RESERVE':
      return CASH_RESERVE_KEY
    case 'ACCRUED_INTEREST':
      return ACCRUED_INTEREST_KEY
    case 'AP_BANK_ACCOUNT':
      return AP_BANK_ACCOUNT_KEY
    case 'AR_BANK_ACCOUNT':
      return AR_BANK_ACCOUNT_KEY
    case 'LIABILITY':
      return LIABILITY_KEY
    case 'FUEL_ADVANCE':
      return FUEL_ADVANCE_KEY
    case 'UNAPPLIED':
      return UNAPPLIED_KEY
    case 'WRITE_OFFS':
      return WRITE_OFFS_KEY
    case 'OVER_ADVANCE':
      return OVER_ADVANCE_KEY
    default:
      return ''
  }
}

export const getCreditOrDebitByLedgerType = (amountType: string, ledgerNameType?: string) => {
  if (!isDefined(amountType) || isBlank(amountType)) return ''
  const debit = strings.DEBIT.toUpperCase()
  const credit = strings.CREDIT.toUpperCase()
  switch (ledgerNameType) {
    case 'ASSETS':
      return amountType === 'INCREASE' ? debit : credit
    case 'REVENUE':
      return amountType === 'INCREASE' ? credit : debit
    case 'LIABILITY':
      return amountType === 'INCREASE' ? credit : debit
    case 'EXPENSE':
      return amountType === 'INCREASE' ? debit : credit
    case 'OVER_ADVANCE':
      return amountType === 'INCREASE' ? debit : credit
    default:
      return ''
  }
}

export const getAmountTypeByLedgerType = (creditDebit: string, LedgerNameType?: string) => {
  if (!isDefined(creditDebit) || isBlank(creditDebit)) return ''
  const debit = strings.DEBIT.toUpperCase()
  switch (LedgerNameType) {
    case 'ASSETS':
      return creditDebit === debit ? 'INCREASE' : 'DECREASE'
    case 'REVENUE':
      return creditDebit === debit ? 'DECREASE' : 'INCREASE'
    case 'LIABILITY':
      return creditDebit === debit ? 'DECREASE' : 'INCREASE'
    case 'EXPENSE':
      return creditDebit === debit ? 'INCREASE' : 'DECREASE'
    case 'OVER_ADVANCE':
      return creditDebit === debit ? 'INCREASE' : 'DECREASE'
    default:
      return ''
  }
}

export const toNumber = (val: string | number) => {
  if (typeof val === 'number') return val
  return parseFloat(val)
}

export function getAutocompleteListValuesReserveRelease(value?: AutocompleteResponse) {
  return value?.data.map((item: AutocompleteInfo) => {
    return {
      key: item.id,
      name: item.name,
      value: item.nReserveReleases,
      companyType: item.companyType,
    }
  })
}

// This function is used to get the value from the form and convert it to a number or null (If the null reset the value)
// Used by TextFormField number type
export const parseNumericValueFromForm = (value?: number | string): number | null => {
  let safeValue = null
  if (isDefined(value) && value !== '') {
    safeValue = Number(value)
  }
  return safeValue
}

// This function is used to get the value from the form and convert it to a boolean or null (If the null reset the value).
// Used by DropdownFormField
export const parseBooleanValueFromForm = (value?: string): boolean | null => {
  let safeValue = null
  if (isDefined(value) && value !== '') {
    safeValue = value === '1'
  }
  return safeValue
}

// This function is used to get the value from the form and convert it to a percentage or null (If the null reset the value).
// Used by TextFormField number type with percentage
export const parsePercentageValueFromForm = (value?: number | string): number | null => {
  let safeValue = null
  if (isDefined(value) && value !== '') {
    safeValue = toDecPercent(value)
    return Number(safeValue)
  }
  return safeValue
}

// This function is used to get the value from BE and convert to YES, NO or '' if the dropdown has an empty option
export const getValueForDropdownWithEmptyOption = (value?: boolean) => {
  if (isDefined(value)) {
    return value ? '1' : '0'
  }
  return ''
}

/**
 * Maps a min/max number filter object to the corresponding states to be sent to the API
 * @param rangeList one list with two values, [0] is for the min, [1] is for the max
 * @param setMin the function to set the min value in state
 * @param setMax the function to set the max value in state
 */
export const mapMinMaxFilterToFilterStates = (
  rangeList: [number | undefined, number | undefined],
  setMin: (amount: number | undefined) => void,
  setMax: (amount: number | undefined) => void,
  withDecimalConversion = false
) => {
  if (rangeList) {
    rangeList[0] === undefined
      ? setMin(undefined)
      : setMin(withDecimalConversion ? Number(toDecPercent(rangeList[0])) : rangeList[0])
    rangeList[1] === undefined
      ? setMax(undefined)
      : setMax(withDecimalConversion ? Number(toDecPercent(rangeList[1])) : rangeList[1])
  } else {
    setMin(undefined)
    setMax(undefined)
  }
}

export const handleStateLabel = (stateType?: string) => {
  switch (stateType) {
    case 'PROVINCE':
      return strings.PROVINCE
    case 'REGION':
      return strings.REGION
    case 'DISTRICT':
      return strings.DISTRICT
    default:
      return strings.STATE
  }
}

// FC2-4317: blob-download mutations (e.g. `exportScotiaFile`) request `responseType: 'blob'`,
// so axios applies that to error responses too — `error.response.data` arrives as a Blob of the
// JSON error body rather than a parsed object. Decode it so `errorMsg`/`getErrorsFromErrorResponse`
// can read `.message`/`.errors` as usual. Shared across the Scotia EFT download call sites
// (DisbursementPreview, InvoiceGroupList, DisbursementList) to avoid re-implementing it per file.
export async function normalizeBlobError(error: any) {
  if (error?.response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await error.response.data.text())
      return { ...error, response: { ...error.response, data: parsed } }
    } catch {
      return error
    }
  }
  return error
}

export function getErrorsFromErrorResponse(error: any): string[] {
  if (isDefined(error)) {
    if (isDefined(error.response)) {
      if (isDefined(error.response.data)) {
        const errors = error.response.data.errors
        if (Array.isArray(errors)) {
          return errors
        }
      }
    }
  }
  return []
}

export function sortFiltersAlphabetic(filters: IFilterList[] | undefined) {
  return filters?.sort((a, b) => a.label.localeCompare(b.label))
}

export function getDropdownOptionsAutocomplete(autocompleteResponse: AutocompleteResponse | undefined) {
  return autocompleteResponse?.data?.map((item) => ({
    label: item.name,
    value: item.id,
  }))
}

export function mapClientOrBrokerFilterStates(
  companyClientId: string,
  setFilterClientId: (value: string | undefined) => void,
  setFilterBrokerId: (value: string | undefined) => void,
  clientBrokerList: AutocompleteResponse | undefined
) {
  if (isNotBlank(companyClientId)) {
    if (!isClient(companyClientId, clientBrokerList)) {
      setFilterClientId('')
      setFilterBrokerId(companyClientId)
    } else {
      setFilterClientId(companyClientId)
      setFilterBrokerId('')
    }
  } else {
    setFilterClientId('')
    setFilterBrokerId('')
  }
}

export function mapLabelFilterStates(labels: any, setLabel: (value: string | undefined) => void) {
  if (isDefined(labels)) {
    if (labels === '') {
      setLabel(undefined)
    } else {
      setLabel(labels.map((label: any) => label.value).join(','))
    }
  } else {
    setLabel(undefined)
  }
}

export function isTrue(value?: boolean) {
  if (!isDefined(value)) return false
  return value === true
}

export function isFalse(value?: boolean) {
  if (!isDefined(value)) return false
  return value === false
}

export function isNotTrue(value?: boolean) {
  return !isTrue(value)
}

export function isNotFalse(value?: boolean) {
  return !isFalse(value)
}

export function stringEquals(value?: string, compare?: string) {
  if (!isDefined(value) || !isDefined(compare)) return false
  return value === compare
}

export function booleanToString(value?: boolean) {
  if (!isDefined(value)) return '0'
  return value ? '1' : '0'
}

export function stringToBoolean(value?: string) {
  if (!isDefined(value)) return false
  return value === '1'
}

export function getFactorSettingsFromLocalStorage() {
  const factorSettings = JSON.parse(localStorage.getItem(LS_FACTOR_SETTINGS) || '{}')
  if (isDefined(factorSettings)) {
    return factorSettings
  }
  return {}
}

export function existNegativeCreditLimits(creditLimit: CreditLimitInfoResponse) {
  const addTypeToEntities = (entities: any[], type: string) => {
    return entities.map((entity) => ({ ...entity, type }))
  }

  const client = creditLimit.client ? [{ ...creditLimit.client, type: 'client' }] : []
  const broker = creditLimit.broker ? [{ ...creditLimit.broker, type: 'broker' }] : []
  const debtors = addTypeToEntities(
    (creditLimit.debtors || []).filter((debtor) => !debtor.parentName),
    'debtor'
  )
  const clientDebtors = addTypeToEntities(creditLimit.clientDebtors || [], 'relationShip')
  const brokerDebtors = addTypeToEntities(creditLimit.brokerDebtors || [], 'relationShip')
  const childDebtors = addTypeToEntities(
    (creditLimit.debtors || []).filter((debtor) => debtor.parentName),
    'childDebtor'
  )

  const allEntities = [...client, ...broker, ...debtors, ...clientDebtors, ...brokerDebtors, ...childDebtors].filter(
    (entity) => entity.negativeCreditLimit
  )

  const hasNegativeCreditLimit = allEntities.some((entity) => entity.negativeCreditLimit)

  const negativeCreditLimits = { allEntities, hasNegativeCreditLimit }
  const clientBrokerCreditLimit = creditLimit.client || creditLimit.broker

  return { negativeCreditLimits, clientBrokerCreditLimit }
}

export function goe(value?: number, compare?: number) {
  if (!isDefined(value) || !isDefined(compare)) return false
  return value >= compare
}

export function eq(value?: number, compare?: number) {
  if (!isDefined(value) || !isDefined(compare)) return false
  return value === compare
}

export function neq(value?: number, compare?: number) {
  if (!isDefined(value) || !isDefined(compare)) return false
  return value !== compare
}

/**
 * Method to return the value if it is different from the compare value. Used to know if the value has changed on form fields.
 * @param orginalValue the value to compare with. Usually the value from the BE or the initial value.
 * @param newValue the value to compare. Usually the value from the form field
 * @returns the value if it has changed, otherwise undefined
 * @case1
 * If both values are undefined, return undefined.
 * @case2
 * If the orginalValue is undefined, return the newValue. That means that the value has changed.
 * @case3
 * If the newValue is undefined, return undefined. That means that the value has not changed.
 * @case4
 * If the orginalValue are different, return the newValue. That means that the value has changed.
 */

export function returnIfChanged(orginalValue?: any, newValue?: any) {
  if (!isDefined(orginalValue) && !isDefined(newValue)) return undefined
  if (!isDefined(orginalValue)) return newValue
  if (!isDefined(newValue)) return undefined
  if (!eq(orginalValue, newValue)) return newValue
}

/**
 * Rounds up a number to the next "nice" number.
 *
 * This function uses a predefined set of commonly used scaling steps
 * (like 1, 1.1, 1.25, 1.5, etc.) to round a given number up to a
 * more human-readable and graph-friendly value.
 *
 * For example:
 * - 1623 -> 2000
 * - 1340 -> 1500
 * - 1234 → 1250
 * - 123  → 125
 * - 44   → 50
 *
 * @param num - The number to round up.
 * @returns The next "nice" number greater than or equal to the input.
 */
export function roundUpToNiceNumber(num: number): number {
  if (num <= 10) return 10

  const exponent = Math.floor(Math.log10(num))
  const base = Math.pow(10, exponent)

  // Nice steps are the usual steps used in charts and graphs
  const niceSteps = [1, 1.1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 9, 10]

  for (const step of niceSteps) {
    const candidate = step * base
    if (candidate >= num) {
      return candidate
    }
  }

  // Fallback in case all steps are below the number
  return 10 * base
}

/**
 * Formats a number into a human-readable abbreviated currency string.
 *
 * Depending on the magnitude of the number, it appends a suffix:
 * - 'K' for thousands (>= 1,000 and < 1,000,000)
 * - 'M' for millions (>= 1,000,000 and < 1,000,000,000)
 * - 'B' for billions (>= 1,000,000,000)
 * If the number is less than 1,000, no abbreviation is added.
 *
 * Example outputs:
 * - 950 → "$950"
 * - 1500 → "$1.5K"
 * - 2500000 → "$2.5M"
 * - 7800000000 → "$7.8B"
 *
 * @param number - The number to be formatted. Defaults to 0 if not provided.
 * @returns A formatted currency string with the appropriate abbreviation.
 */
export function getNumberWithQuantityAbbreviation(number: number = 0): string {
  let value: number
  let abbreviation: string

  const exponent: number = Math.floor(Math.log10(number))

  if (exponent >= 3 && exponent < 6) {
    value = number / 1000
    abbreviation = 'K'
  } else if (exponent >= 6 && exponent < 9) {
    value = number / 1000000
    abbreviation = 'M'
  } else if (exponent >= 9) {
    value = number / 1000000000
    abbreviation = 'B'
  } else {
    value = number
    abbreviation = ''
  }

  return `${currencyFormat(value, 'USD', 0)}${abbreviation}`
}

export function mapDateFilters(
  date: any,
  setStartDate: (value: Date | undefined) => void,
  setEndDate: (value: Date | undefined) => void
) {
  setStartDate(undefined)
  setEndDate(undefined)
  if (isDefined(date)) {
    if (isDefined(date[0])) {
      if (date[0].format) {
        setStartDate(date[0].format('YYYY-MM-DD') as any)
      } else {
        setStartDate(date[0])
      }
    } else {
      setStartDate(undefined)
    }
    if (isDefined(date[1])) {
      if (date[1].format) {
        setEndDate(date[1].format('YYYY-MM-DD') as any)
      } else {
        setEndDate(date[1])
      }
    } else {
      setEndDate(undefined)
    }
  }
}

export function genericHandleSortModelChange(model: GridSortModel, setSortValue?: (value: string) => void) {
  if (!isDefined(setSortValue)) return
  if (model.length > 0) {
    setSortValue(`${model[0].field}:${model[0].sort?.toUpperCase()}`)
  } else {
    setSortValue('')
  }
}
const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object'

export function getRemitForInvoicesEnabled(company: any): boolean {
  return isObject(company) && typeof company.remitForInvoicesEnabled === 'boolean'
    ? company.remitForInvoicesEnabled
    : false
}

export function getRemitForFuelAdvancesEnabled(company: any): boolean {
  return isObject(company) && typeof company.remitForFuelAdvancesEnabled === 'boolean'
    ? company.remitForFuelAdvancesEnabled
    : false
}

export function getRemitForReserveReleaseEnabled(company: any): boolean {
  return isObject(company) && typeof company.remitForReserveReleaseEnabled === 'boolean'
    ? company.remitForReserveReleaseEnabled
    : false
}

export function hastNextPage(headers?: AxiosResponseHeaders) {
  const rawValue = headers?.[PAGINATION_HAS_NEXT_PAGE]
  if (Array.isArray(rawValue)) {
    return rawValue.some((value) => String(value).trim().toLowerCase() === 'true')
  }
  return String(rawValue)
    .split(',')
    .some((value) => value.trim().toLowerCase() === 'true')
}

export function haveSameElements<T>(a: T[], b: T[]): boolean {
  if (!isDefined(a) || !isDefined(b)) return false
  if (a.length !== b.length) return false
  const sortBy = (x: T[]) => [...x].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
  return JSON.stringify(sortBy(a)) === JSON.stringify(sortBy(b))
}
